import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryApi } from '@reduxjs/toolkit/query/react';
import _ from 'lodash';

import { BSAEnv } from '@appTypes/enums';
import type {
  AddItemPurchaseOrderReq,
  AdvancementHistoryItem,
  AdvancementHistoryRequest,
  DeleteEventsCommunicationsRequestParams,
  DeleteEventsCommunicationsResponse,
  DeleteSubUnitResponse,
  DeleteUserFromSubUnitResponse,
  GetActivitesRequest,
  GetActivitesRequestParams,
  GetActivitiesResponse,
  GetAdventureDetailsResponse,
  GetAdventureRequirementsReq,
  GetAdventureRequirementsRes,
  GetAdventuresReq,
  GetAdventuresRes,
  GetAwardDetailsResponse,
  GetAwardRequirementsResponse,
  GetCompletePersonProfileRes,
  GetEventsCommunicationsOrganizationsRequestParams,
  GetEventsCommunicationsOrganizationsResponse,
  GetGeneralAdvancementDetailsResponse,
  GetMeritBadgeDetailsResponse,
  GetMeritBadgeRequirementsReq,
  GetMeritBadgeRequirementsRes,
  GetOrgAdvancementsReadyToAwardParams,
  GetOrgAdvancementsReadyToAwardRequest,
  GetOrgAdvancementsReadyToAwardResponse,
  GetOrgPositionsResponse,
  GetOrgProfileResponse,
  GetOrgSubUnitsResponse,
  GetOrganizationOnboardUrlResponse,
  GetOrganizationPaymentProfileResponse,
  GetPersonProfileResponse,
  GetPersonTrainingYPTResponse,
  GetPurchaseOrderDetailsResponse,
  GetRankDetailsResponse,
  GetRankRequirementsReq,
  GetRankRequirementsRes,
  GetRanksReq,
  GetRanksRes,
  GetRenewalRelationshipsRequest,
  GetRenewalRelationshipsResponse,
  GetSSElectiveDetailsResponse,
  GetSubUnitAdultsResponse,
  GetSubUnitYouthsResponse,
  GetUnitDenChiefsRes,
  GetUnitPurchaseOrdersRequest,
  GetUnitPurchaseOrdersResponse,
  GetUserActivitySummaryResponse,
  GetUserAdventureRequirementsReq,
  GetUserAdventureRequirementsRes,
  GetUserAwardRequirementsRequest,
  GetUserAwardRequirementsResponse,
  GetUserMeritBadgeRequirementsRequest,
  GetUserMeritBadgeRequirementsResponse,
  GetUserMystProfileResponse,
  GetUserRankVersionRequirementsRes,
  GetUserRoleTypesRequest,
  GetUserRoleTypesResponse,
  GetUserSubscriptionResponse,
  GetYouthAdventureRequirementsRes,
  GetYouthMeritBadgeDetailsRes,
  GetYouthRankRequirementsRes,
  OrganizationActivitiesDashboard,
  OrganizationActivitiesDashboardRequestParams,
  OrganizationAdvDashboard,
  POSTSubscriptionRequest,
  PUTSubscriptionRequest,
  PostAddAdultsToSubUnitRequest,
  PostAddAdultsToSubUnitResponse,
  PostAddSubUnitRequest,
  PostAddSubUnitResponse,
  PostAddYouthsToSubUnitRequest,
  PostAddYouthsToSubUnitResponse,
  PostAssignFunctionalRequest,
  PostAssignFunctionalResponse,
  PostAwardRequest,
  PostAwardRequirementsRequest,
  PostAwardRequirementsResponse,
  PostAwardResponse,
  PostBulkMeritBadgeRequirementsReq,
  PostBulkMeritBadgeRequirementsRes,
  PostBulkYouthRankRequirementsReq,
  PostBulkYouthRankRequirementsRes,
  PostDeleteProfileInfoRequest,
  PostEventsCommunicationsRequestBody,
  PostEventsCommunicationsResponse,
  PostExpireFunctionalRequest,
  PostExpireFunctionalResponse,
  PostGetSbExtendedProfilesReq,
  PostGetSbExtendedProfilesRes,
  PostMembershipRegistrationsRequest,
  PostMembershipRegistrationsResponse,
  PostOrganizationPaymentProfileRequest,
  PostOrganizationPaymentProfileResponse,
  PostPermissionSlipProfilesRequest,
  PostPermissionSlipProfilesResponse,
  PostPictureUploadRequest,
  PostPictureUploadResponse,
  PostProfilePictureRequest,
  PostReassignPositionsRequest,
  PostReassignPositionsResponse,
  PostSbEducationRecordsReq,
  PostSbEducationRecordsRes,
  PostSbExtendedProfileReq,
  PostSbExtendedProfileReqRes,
  PostSbSchoolInformationReq,
  PostSbSchoolInformationRes,
  PostSearchMBCRequest,
  PostSearchMBCResponse,
  PostUnapproveAdvancementRequest,
  PostUserAwardRequest,
  PostUserAwardRequirementRequest,
  PostUserAwardRequirementResponse,
  PostUserAwardResponse,
  PostYouthAdventuresRequirementsRequest,
  PostYouthAdventuresRequirementsResponse,
  PostYouthMeritBadgesReq,
  PostYouthMeritBadgesRes,
  PreciselyAuthTokenQueryParams,
  PreciselyAuthTokenResponse,
  PutAkelaProfileRequest,
  PutOrganizationPaymentProfileRequest,
  PutOrganizationPaymentProfileResponse,
  PutSBProfileRequest,
  PutSubUnitRequest,
  PutSubUnitResponse,
  PutUpdateAwardsRequest,
  PutUpdateYouthAdvancementRequest,
  PutUpdateYouthAdvancementResponse,
  PutUpdateYouthAwardRequest,
  PutYouthMeritBadgeVersionRequest,
  PutYouthMeritBadgeVersionResponse,
  PutYouthRankVersionRequest,
  PutYouthRankVersionResponse,
  UpdateMembershipRegistrationRequest,
  UpdateMembershipRegistrationResponse,
  UpdatePurchaseOrderDetailsReq,
} from '@appTypes/esb';
import { API_ENV, ESB_API_URLS, IS_LOCAL_ENV, RTK_PROXY } from '@config/config';
import type { ConfigState } from '@config/types';
import { toPurchaseStatusesString } from '@modules/advancement/constants';
import { mapAdvancementReportDtoToModel } from '@modules/advancementReport/duck/services.utils';
import type { AdvancementReportData } from '@modules/advancementReport/types';
import { Positions } from '@modules/permissionManager/constants';
import { advancementTypes } from '@modules/shared/constants';
import type { AdvancementTypesType } from '@modules/shared/constants';
import type { NormalizedOrgProfile } from '@modules/unit/types';
import { transformOrgProfile } from '@unit/duck/utils';

import { tagTypes } from './constants';
import { dynamicBaseQuery } from './utils';
import {
  type AdvancementDetailsArgs,
  type GeneralAdvancementDetailsArgs,
  getAdvancementDetailsQuery,
  getGeneralAdvancementDetailsQuery,
} from './utils/advancementDetailsObject';
import tagFactory, { type TagType } from './utils/tagFactory';

/** kept in sync with the matching route in tools/proxyConfig.js */
const LOCAL_ESB_PROXY_PATH = '/apis/rtk-esb';

function getESBBaseUrl(_args: unknown, api: BaseQueryApi): string {
  // ESB only allows a couple of localhost origins, so go through the dev proxy
  // instead and keep every request same-origin whatever port the server is on
  if (IS_LOCAL_ENV && RTK_PROXY) {
    return LOCAL_ESB_PROXY_PATH;
  }

  const state = api.getState() as Record<string, unknown>;

  const stateApiEnv = (state?.config as ConfigState)?.apiEnv as string;
  const finalApiEnv = (stateApiEnv ? stateApiEnv : API_ENV) as BSAEnv;

  const baseUrl = ESB_API_URLS[finalApiEnv];
  return baseUrl;
}

/** Reference data: a rank version's requirement text never changes */
const ONE_DAY = 60 * 60 * 24;
const THIRTY_MINUTES = 60 * 30;

export const esbApi = createApi({
  reducerPath: 'esbApi',
  tagTypes: Object.keys(tagTypes),
  baseQuery: dynamicBaseQuery(getESBBaseUrl),
  endpoints: builder => ({
    /**
     * @esbEndpoint GET /persons/:personGuid/roleTypes
     */
    getRoleTypes: builder.query<
      GetUserRoleTypesResponse,
      {
        personGuid: string;
        params: GetUserRoleTypesRequest;
      }
    >({
      query: arg => ({
        url: `/persons/${arg.personGuid}/roleTypes`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, error, arg) => {
        if (error || !result) return [];
        return [{ type: tagTypes.UserRoleTypes, id: arg.personGuid }];
      },
    }),
    /**
     * @esbEndpoint GET /organizations/v2/:orgGuid/profile
     */
    getOrganizationProfile: builder.query<
      NormalizedOrgProfile,
      {
        orgGuid: string;
      }
    >({
      query: ({ orgGuid }) => ({
        url: `/organizations/v2/${orgGuid}/profile`,
        method: 'GET',
      }),
      transformResponse: (result: GetOrgProfileResponse) =>
        transformOrgProfile(result),
      providesTags: (result, _error, { orgGuid }) =>
        result ? [{ type: tagTypes.OrganizationProfile, id: orgGuid }] : [],
    }),

    /**
     * @esbEndpoint GET /organizations/v2/payment/:organizationGuid/stripe/onboardURL
     */
    getOrganizationOnboardUrl: builder.query<
      GetOrganizationOnboardUrlResponse,
      {
        orgGuid: string;
      }
    >({
      query: ({ orgGuid }) => ({
        url: `/organizations/v2/payment/${orgGuid}/stripe/onboardURL`,
        method: 'GET',
      }),
    }),

    /**
     * @esbEndpoint GET /organizations/v2/payment/:organizationGuid/stripe/profile
     */
    getOrganizationPaymentProfile: builder.query<
      GetOrganizationPaymentProfileResponse,
      {
        orgGuid: string;
      }
    >({
      query: ({ orgGuid }) => ({
        url: `/organizations/v2/payment/${orgGuid}/stripe/profile`,
        method: 'GET',
      }),
      providesTags: (result, _error, { orgGuid }) =>
        result
          ? [{ type: tagTypes.OrganizationPaymentProfile, id: orgGuid }]
          : [],
    }),

    /**
     * @esbEndpoint POST /organizations/v2/payment/:organizationGuid/stripe/profile
     */
    postOrganizationPaymentProfile: builder.mutation<
      PostOrganizationPaymentProfileResponse,
      {
        orgGuid: string;
        payload: PostOrganizationPaymentProfileRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/payment/${arg.orgGuid}/stripe/profile`,
        method: 'POST',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [{ type: tagTypes.OrganizationPaymentProfile, id: arg.orgGuid }],
    }),

    /**
     * @esbEndpoint PUT /organizations/v2/payment/:organizationGuid/stripe/profile
     */
    putOrganizationPaymentProfile: builder.mutation<
      PutOrganizationPaymentProfileResponse,
      {
        orgGuid: string;
        payload: PutOrganizationPaymentProfileRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/payment/${arg.orgGuid}/stripe/profile`,
        method: 'PUT',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [{ type: tagTypes.OrganizationPaymentProfile, id: arg.orgGuid }],
    }),

    /**
     * @esbEndpoint GET /organizations/v2/:organizationGuid/advancementDashboard
     */
    getOrgAdvancementsDashboard: builder.query<
      OrganizationAdvDashboard,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/${arg.organizationGuid}/advancementDashboard`,
        method: 'GET',
      }),
      providesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.OrganizationAdvDashboard,
                id: arg.organizationGuid,
              },
            ],
    }),

    /**
     * @esbEndpoint GET /organizations/v2/:arg.organizationGuid/unitActivitiesDashboard
     */
    getOrgActivityDashboard: builder.query<
      OrganizationActivitiesDashboard,
      OrganizationActivitiesDashboardRequestParams
    >({
      query: arg => ({
        url: `/organizations/v2/${arg.organizationGuid}/unitActivitiesDashboard`,
        method: 'GET',
        params: {
          completedActivities: true,
          startDate: arg.startDate,
        },
      }),
      providesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.OrganizationActivitiesDashboard,
                id: arg.organizationGuid,
              },
            ],
    }),
    /**
     * @esbEndpoint GET /advancements/v2/:advancementType/:advancementId
     */
    getGeneralAdvancementDetails: builder.query<
      GetGeneralAdvancementDetailsResponse,
      GeneralAdvancementDetailsArgs
    >(getGeneralAdvancementDetailsQuery()),
    /**
     * @esbEndpoint GET /advancements/v2/adventures/:advancementId
     */
    getAdventureDetails: builder.query<
      GetAdventureDetailsResponse,
      AdvancementDetailsArgs
    >(getAdvancementDetailsQuery(advancementTypes.ADVENTURES)),

    /**
     * @esbEndpoint GET /advancements/v2/meritBadges/:advancementId
     */
    getMeritBadgeDetails: builder.query<
      GetMeritBadgeDetailsResponse,
      AdvancementDetailsArgs
    >(getAdvancementDetailsQuery(advancementTypes.MERIT_BADGES)),

    /**
     * @esbEndpoint POST /advancements/v2/meritBadges/counselors/search
     */
    postMBCounselorsSearch: builder.query<
      PostSearchMBCResponse,
      {
        body: PostSearchMBCRequest;
      }
    >({
      query: arg => ({
        url: '/advancements/v2/meritBadges/counselors/search',
        method: 'POST',
        body: arg.body,
      }),
    }),

    /**
     * @esbEndpoint GET /advancements/v2/ranks/:advancementId
     */
    getRankDetails: builder.query<
      GetRankDetailsResponse,
      AdvancementDetailsArgs
    >(getAdvancementDetailsQuery(advancementTypes.RANKS)),

    /**
     * @esbEndpoint GET /advancements/v2/ssElectives/:advancementId
     */
    getSSElectiveDetails: builder.query<
      GetSSElectiveDetailsResponse,
      AdvancementDetailsArgs
    >(getAdvancementDetailsQuery(advancementTypes.SSELECTIVES)),
    /**
     * @esbEndpoint GET /advancements/awards/:advancementId
     */
    getAwardDetails: builder.query<
      GetAwardDetailsResponse,
      AdvancementDetailsArgs
    >(getAdvancementDetailsQuery(advancementTypes.AWARDS)),
    /**
     * @esbEndpoint GET /advancements/v2/organization/:organizationGuid/ranks/:rankId/userRequirements
     */
    getBulkUserRankRequirements: builder.query<
      GetUserRankVersionRequirementsRes,
      {
        orgGuid: string;
        rankId: number;
        memberIds: number[];
      }
    >({
      query: arg => {
        const { orgGuid, rankId, memberIds } = arg;
        return {
          url: `/advancements/v2/organization/${orgGuid}/ranks/${rankId}/userRequirements`,
          method: 'GET',
          params: {
            memberId: memberIds,
          },
        };
      },
      providesTags: (result, error) =>
        result && !error
          ? result.map(rankInfo => ({
              type: tagTypes.YouthRankVersionDetails,
              id: `${rankInfo.userId}-${rankInfo.rankId}`,
            }))
          : [],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/organization/:organizationGuid/adventures/:adventureId/userRequirements
     */
    getBulkUserAdventureRequirements: builder.query<
      GetUserAdventureRequirementsRes,
      {
        orgGuid: string;
        adventureId: number;
        params: GetUserAdventureRequirementsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGuid}/adventures/${arg.adventureId}/userRequirements`,
        method: 'GET',
        params: {
          memberId: arg.params.memberId,
        },
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.AdventureUserRequirements,
                id: `${arg.orgGuid}-${arg.adventureId}`,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/organization/:organizationGuid/awards/:awardId/userRequirements
     */
    getBulkUserAwardRequirements: builder.query<
      GetUserAwardRequirementsResponse,
      {
        orgGuid: string;
        awardId: number;
        params: GetUserAwardRequirementsRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGuid}/awards/${arg.awardId}/userRequirements`,
        method: 'GET',
        params: {
          memberId: arg.params.memberId,
        },
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              tagFactory.AwardUserRequirements.orgRequirements(
                arg.orgGuid,
                arg.awardId,
              ),
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/organization/:organizationGuid/meritBadges/:meritBadgeId/userRequirements
     */
    getBulkUserMeritBadgeRequirements: builder.query<
      GetUserMeritBadgeRequirementsResponse,
      {
        orgGuid: string;
        meritBadgeId: number;
        params: GetUserMeritBadgeRequirementsRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGuid}/meritBadges/${arg.meritBadgeId}/userRequirements`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, error, arg) => {
        const tags = [
          tagFactory.MeritBadgeUserRequirements.byMeritBadge(
            arg.orgGuid,
            arg.meritBadgeId,
          ),
        ];

        if (!result || error) return [];

        return tags;
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/meritBadges/:meritBadgeId
     */
    getYouthMeritBadgeDetails: builder.query<
      GetYouthMeritBadgeDetailsRes,
      {
        userId: number;
        meritBadgeId: number;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/meritBadges/${arg.meritBadgeId}`,
        method: 'GET',
      }),
      providesTags: (_result, error, arg) =>
        error
          ? []
          : [
              tagFactory.YouthMeritBadge.youthMBDetails(
                arg.userId,
                arg.meritBadgeId,
              ),
            ],
    }),

    /**
     * @esbEndpoint PUT /advancements/v2/:userId/meritBadges/:meritBadgeId/version
     */
    updateYouthMeritBadgeVersion: builder.mutation<
      PutYouthMeritBadgeVersionResponse,
      {
        meritBadgeId: number | string;
        payload: PutYouthMeritBadgeVersionRequest;
        userId: string;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.userId}/meritBadges/${arg.meritBadgeId}/version`,
        method: 'PUT',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        return [
          tagFactory.YouthMeritBadge.youthMBDetails(
            arg.userId,
            arg.meritBadgeId,
          ),
        ];
      },
    }),

    /**
     * Moves the youth onto a different version of a rank. Requirement ids are
     * version-scoped, so this rewrites which requirements they are working
     * against and does NOT carry progress across
     *
     * @esbEndpoint PUT /advancements/v2/:userId/ranks/:rankId/version
     */
    updateYouthRankVersion: builder.mutation<
      PutYouthRankVersionResponse,
      {
        rankId: number;
        userId: number;
        payload: PutYouthRankVersionRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.userId}/ranks/${arg.rankId}/version`,
        method: 'PUT',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        return [
          tagFactory.YouthRankRequirements.youthRankRequirements(
            arg.userId,
            arg.rankId,
          ),
          tagFactory.YouthRanks.youthRanksList(arg.userId),
        ];
      },
    }),

    /**
     * === Advancements V1
     */
    /**
     * @esbEndpoint GET /advancements/adventures/:adventureId/requirements
     */
    getAdventureRequirements: builder.query<
      GetAdventureRequirementsRes,
      { adventureId: number; params: GetAdventureRequirementsReq }
    >({
      query: arg => ({
        url: `/advancements/adventures/${arg.adventureId}/requirements`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, _error, { adventureId }) =>
        result?.requirements
          ? [tagFactory.AdventureRequirements.requirements(adventureId)]
          : [],
    }),
    /**
     * @esbEndpoint GET /advancements/meritBadges/:meritBadgeId/requirements
     */
    getMeritBadgeRequirements: builder.query<
      GetMeritBadgeRequirementsRes,
      { meritBadgeId: number; params: GetMeritBadgeRequirementsReq }
    >({
      query: arg => ({
        url: `/advancements/meritBadges/${arg.meritBadgeId}/requirements`,
        method: 'GET',
        params: arg.params,
      }),
    }),

    /**
     * @esbEndpoint POST /advancements/youth/meritBadges
     */
    postYouthMeritBadges: builder.mutation<
      PostYouthMeritBadgesRes,
      PostYouthMeritBadgesReq
    >({
      query: body => ({
        url: `/advancements/youth/meritBadges`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const orgGuids = _.uniq(arg.map(item => item.organizationGuid));

        return orgGuids.flatMap(orgGuid => [
          {
            // invalidate the counts to approve tab
            type: tagTypes.OrganizationAdvDashboard,
            id: orgGuid,
          },
          {
            // refresh to approve list
            type: tagTypes.AdvancementHistory,
            id: `Unit-List-${orgGuid}`,
          },
        ]);
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/youth/adventures/:adventureId/requirements
     */
    postYouthAdventuresRequirements: builder.mutation<
      PostYouthAdventuresRequirementsResponse,
      {
        adventureId: number;
        payload: PostYouthAdventuresRequirementsRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/adventures/${arg.adventureId}/requirements`,
        method: 'POST',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const orgGuids = _.uniq(arg.payload.map(item => item.organizationGuid));

        return orgGuids.flatMap(orgGuid => [
          {
            type: tagTypes.AdventureUserRequirements,
            id: `${orgGuid}-${arg.adventureId}`,
          },
          {
            // invalidate the counts to approve tab
            type: tagTypes.OrganizationAdvDashboard,
            id: orgGuid,
          },
          {
            // refresh to approve list
            type: tagTypes.AdvancementHistory,
            id: `Unit-List-${orgGuid}`,
          },
        ]);
      },
    }),

    /**
     * === Advancements V2
     */

    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/adventures/:adventureId/requirements
     */
    getYouthAdventureRequirements: builder.query<
      GetYouthAdventureRequirementsRes,
      { adventureId: number; userId: number }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/adventures/${arg.adventureId}/requirements`,
        method: 'GET',
      }),
      //
      providesTags: (result, _error, { adventureId, userId }) =>
        result?.requirements
          ? [
              tagFactory.YouthAdventureRequirements.requirements(
                userId,
                adventureId,
              ),
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /persons/v2/:userId/personprofile
     */
    getPersonProfile: builder.query<
      GetPersonProfileResponse,
      {
        userId: number;
      }
    >({
      query: ({ userId }) => ({
        url: `/persons/v2/${userId}/personprofile`,
        method: 'GET',
      }),
      providesTags: (result, error, { userId }) => {
        if (error || !result) return [];
        return [tagFactory.PersonProfile.byUserId(userId)];
      },
    }),

    /**
     * @esbEndpoint GET /persons/v2/:personGuid/personProfile
     */
    getCompletePersonProfile: builder.query<
      GetCompletePersonProfileRes,
      {
        personGuid: string;
      }
    >({
      query: ({ personGuid }) => ({
        url: `/persons/v2/${personGuid}/personprofile`,
        method: 'GET',
      }),
      providesTags: (result, error, { personGuid }) =>
        result && !error
          ? [
              tagFactory.CompletePersonProfile.personProfile(personGuid),
              tagFactory.CompletePersonProfile.personProfileUserId(
                result?.profile?.userId,
              ),
              tagFactory.CompletePersonProfile.personProfileMID(
                Number(result?.profile?.memberId),
              ),
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /persons/:personGuid/subscriptions
     */
    getUserSubscriptions: builder.query<
      GetUserSubscriptionResponse,
      {
        personGuid: string;
      }
    >({
      query: ({ personGuid }) => ({
        url: `/persons/${personGuid}/subscriptions`,
        method: 'GET',
      }),
      providesTags: (result, _error, { personGuid }) =>
        result ? [{ type: tagTypes.PersonSubscriptions, id: personGuid }] : [],
    }),

    /**
     * TODO: review why this is not used
     * @esbEndpoint GET /persons/:personGuid/profile
     */
    getUserMystProfile: builder.query<
      GetUserMystProfileResponse,
      {
        personGuid: string;
      }
    >({
      query: ({ personGuid }) => ({
        url: `/persons/${personGuid}/profile`,
        method: 'GET',
      }),
      providesTags: (result, _error, { personGuid }) =>
        result ? [{ type: tagTypes.PersonMystProfile, id: personGuid }] : [],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/youth/:userId/ranks/:rankId
     */
    getYouthRankDetails: builder.query({
      query: ({ rankId, userId }) => ({
        url: `/advancements/v2/youth/${userId}/ranks/${rankId}`,
        method: 'GET',
      }),
      providesTags: (result, _error, { userId, rankId }) =>
        result
          ? [{ type: tagTypes.YouthRankDetails, id: `${userId}-${rankId}` }]
          : [],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/youth/meritBadges/:meritBadgeId/requirements
     */
    postBulkYouthMeritBadgeRequirements: builder.mutation<
      PostBulkMeritBadgeRequirementsRes,
      {
        meritBadgeId: number;
        payload: PostBulkMeritBadgeRequirementsReq;
      }
    >({
      query: ({ meritBadgeId, payload }) => ({
        url: `/advancements/v2/youth/meritBadges/${meritBadgeId}/requirements`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const orgGuids = _.uniq(arg.payload.map(item => item.organizationGuid));

        const tags = [];

        tags.push(
          ...orgGuids
            .filter(Boolean)
            .flatMap((orgGuid: string) => [
              tagFactory.OrganizationAdvDashboard.unitDashboard(orgGuid),
              tagFactory.AdvancementHistory.unitHistory(orgGuid),
              tagFactory.MeritBadgeUserRequirements.byMeritBadge(
                orgGuid,
                arg.meritBadgeId,
              ),
            ]),
        );

        const youthsMBs = arg.payload.flatMap(item => [
          tagFactory.YouthMeritBadge.youthMBList(item.userId),
          tagFactory.YouthMeritBadge.youthMBDetails(
            item.userId,
            arg.meritBadgeId,
          ),
        ]);
        tags.push(...youthsMBs);

        return tags;
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/youth/ranks/:rankId/requirements
     */
    postBulkYouthRankRequirements: builder.mutation<
      PostBulkYouthRankRequirementsRes,
      { rankId: number; payload: PostBulkYouthRankRequirementsReq }
    >({
      queryFn: async (arg, _api, _opts, baseQuery) => {
        const promises = _.chain(arg.payload)
          .groupBy(item => item.userId)
          .map(youthPayload => {
            const payload: PostBulkYouthRankRequirementsReq = youthPayload;
            return baseQuery({
              url: `/advancements/v2/youth/ranks/${arg.rankId}/requirements`,
              method: 'POST',
              body: payload,
            });
          })
          .value();

        try {
          const results = await Promise.all(promises);
          const data = _.flatMap(
            results.map(res => {
              if ('error' in res) {
                throw res.error;
              } else {
                const result = res.data as PostBulkYouthRankRequirementsRes;
                return result;
              }
            }),
          );

          const errors = data.filter(item =>
            item.requirements.some(item => item.status === 'Fail'),
          );

          if (errors.length > 0) {
            throw { data: errors, status: 400 };
          }

          return {
            data,
          };
        } catch (err) {
          return { error: err };
        }
      },
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const orgGuids = _.uniq(arg.payload.map(item => item.organizationGuid));
        const userIds = _.uniq(arg.payload.map(item => item.userId));

        return [
          // Bulk entry writes the same requirements the youth checklist reads
          ...userIds.flatMap(userId => [
            tagFactory.YouthRankRequirements.youthRankRequirements(
              userId,
              arg.rankId,
            ),
            tagFactory.YouthRanks.youthRanksList(userId),
          ]),
          ...orgGuids.flatMap(orgGuid => [
            tagFactory.OrganizationAdvDashboard.unitDashboard(orgGuid),
            tagFactory.AdvancementHistory.unitHistory(orgGuid),
            // Approving every requirement can approve the rank itself
            tagFactory.UnitRoster.youthRoster(orgGuid),
          ]),
        ];
      },
    }),
    /**
     * @esbEndpoint POST /advancements/v2/youth/ranks/:rankId/requirements
     */
    postYouthRankRequirements: builder.mutation<
      PostBulkYouthRankRequirementsRes,
      { rankId: number; payload: PostBulkYouthRankRequirementsReq }
    >({
      query: ({ rankId, payload }) => ({
        url: `/advancements/v2/youth/ranks/${rankId}/requirements`,
        method: 'POST',
        body: payload,
        /**
         * ESB answers 200 even when it rejects a requirement: the verdict is per
         * requirement inside the body, with a `message` explaining why. Without
         * this the caller reports success on a save that never took. Failing here
         * puts the body on `error.data`
         */
        validateStatus: (
          response: { status: number },
          result: PostBulkYouthRankRequirementsRes,
        ) =>
          response.status === 200 &&
          !result?.some?.(item =>
            item?.requirements?.some(
              requirement => requirement?.status === 'Fail',
            ),
          ),
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const orgGuids = _.uniq(arg.payload.map(item => item.organizationGuid));
        const userIds = _.uniq(arg.payload.map(item => item.userId));

        return [
          // The requirements just written, plus the rank list whose percentage
          // they roll up into
          ...userIds.flatMap(userId => [
            tagFactory.YouthRankRequirements.youthRankRequirements(
              userId,
              arg.rankId,
            ),
            tagFactory.YouthRanks.youthRanksList(userId),
          ]),
          ...orgGuids.flatMap(orgGuid => [
            {
              // invalidate the counts to approve tab
              type: tagTypes.OrganizationAdvDashboard,
              id: orgGuid,
            },
            {
              // refresh to approve list
              type: tagTypes.AdvancementHistory,
              id: `Unit-List-${orgGuid}`,
            },
          ]),
        ];
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/ranks/:rankId/requirements
     */
    getYouthRankRequirements: builder.query<
      GetYouthRankRequirementsRes,
      { rankId: number; userId: number }
    >({
      keepUnusedDataFor: THIRTY_MINUTES,
      query: ({ rankId, userId }) => ({
        url: `/advancements/v2/youth/${userId}/ranks/${rankId}/requirements`,
        method: 'GET',
      }),
      providesTags: (result, error, { userId, rankId }) => {
        if (error || !result) return [];
        return [
          tagFactory.YouthRankRequirements.youthRankRequirements(
            userId,
            rankId,
          ),
        ];
      },
    }),

    // PUBLIC ENDPOINTS (don't require authentication)
    /**
     * @esbEndpoint GET /advancements/v2/ranks
     */
    getRanks: builder.query<
      GetRanksRes,
      | {
          params: GetRanksReq;
        }
      | undefined
    >({
      keepUnusedDataFor: ONE_DAY,
      query: () => ({
        url: `/advancements/v2/ranks`,
        method: 'GET',
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      providesTags: (result, _error, _arg) =>
        result
          ? [
              ...result.map(rank => ({
                type: tagTypes.Rank,
                id: rank.id,
              })),
              { type: tagTypes.Rank, id: 'Ranks-List' },
            ]
          : [],
    }),

    /**
     * @esbEndpoint POST /advancements/advancementHistory
     */
    getAdvancementHistory: builder.query<
      AdvancementHistoryItem[],
      AdvancementHistoryRequest
    >({
      query: arg => ({
        url: '/advancements/advancementHistory',
        method: 'POST',
        body: arg.body,
        params: arg.params,
      }),
      providesTags: (result, error, arg) => {
        if (error || !result) return [];

        return [
          {
            type: tagTypes.AdvancementHistory,
            id: `Unit-List-${arg.body.organizationGuid}`,
          },
          {
            type: tagTypes.AdvancementHistory,
            id: `Unit-${arg.body.organizationGuid}-AdvType-${arg.body.advancementType}`,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/adventures
     */
    getAdventures: builder.query<
      GetAdventuresRes,
      { params: GetAdventuresReq } | undefined
    >({
      query: () => ({
        url: `/advancements/v2/adventures`,
        method: 'GET',
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      providesTags: (result, _error, _arg) =>
        result?.adventures
          ? [
              ...result.adventures.map(adventure => ({
                type: tagTypes.Adventure,
                id: adventure.id,
              })),
              { type: tagTypes.Adventure, id: 'Adventures-List' },
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/ranks/:rankId/requirements?versionId=:versionId
     */
    getRankRequirements: builder.query<
      GetRankRequirementsRes,
      {
        rankId: number;
        params: GetRankRequirementsReq;
      }
    >({
      keepUnusedDataFor: ONE_DAY,
      query: ({ rankId, params }) => ({
        url: `/advancements/v2/ranks/${rankId}/requirements`,
        params,
        method: 'GET',
        validateStatus: (
          response: { status: number },
          result: Record<string, unknown>,
        ) => response.status === 200 && !result.errorCode,
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              tagFactory.RankRequirements.rankRequirements(
                arg.rankId,
                arg.params?.versionId,
              ),
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /persons/:personGuid/renewalRelationships
     */
    getPersonRenewalRelationships: builder.query<
      GetRenewalRelationshipsResponse,
      {
        params?: GetRenewalRelationshipsRequest;
        personGuid: string;
      }
    >({
      query: ({ params, personGuid }) => ({
        url: `/persons/${personGuid}/renewalRelationships`,
        method: 'GET',
        params,
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.PersonRenewalRelationships,
                id: arg.personGuid,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint POST /persons/:personGuid/profilePicture
     */
    createProfilePicture: builder.mutation<
      void,
      {
        personGuid: string;
        body: PostProfilePictureRequest;
      }
    >({
      query: arg => ({
        url: `/persons/${arg.personGuid}/profilePicture`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              { type: tagTypes.CompletePersonProfile, id: arg.personGuid },
              {
                type: tagTypes.UnitRoster,
                id: `RosterAdult-${arg.personGuid}`,
              },
            ],
    }),

    /**
     * @esbEndpoint PUT /persons/:personGuid/profilePicture
     */
    updateProfilePicture: builder.mutation<
      void,
      {
        personGuid: string;
        body: PostProfilePictureRequest;
      }
    >({
      query: arg => ({
        url: `/persons/${arg.personGuid}/profilePicture`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              { type: tagTypes.CompletePersonProfile, id: arg.personGuid },
              {
                type: tagTypes.UnitRoster,
                id: `RosterAdult-${arg.personGuid}`,
              },
            ],
    }),

    /**
     * @esbEndpoint POST /advancements/:userId/pictureUpload
     */
    createPictureUpload: builder.mutation<
      PostPictureUploadResponse,
      {
        userId: number;
        body: PostPictureUploadRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/${arg.userId}/pictureUpload`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error ? [] : [{ type: tagTypes.PersonProfile, id: arg.userId }],
    }),

    /**
     * @esbEndpoint POST /advancements/:orgGuid/sbEducationRecords
     */
    getEducationRecords: builder.query<
      PostSbEducationRecordsRes,
      {
        orgGuid: string;
        body: PostSbEducationRecordsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/${arg.orgGuid}/sbEducationRecords`,
        method: 'POST',
        body: arg.body,
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];
        const tags = [
          tagFactory.SbEducationRecords.sbEducationRecords(arg.orgGuid),
        ];

        return tags;
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/{orgGuid}/sbSchoolInformation/quickEntry
     */
    postSbSchoolInformation: builder.mutation<
      PostSbSchoolInformationRes,
      {
        orgGuid: string;
        personGuid?: string;
        body: PostSbSchoolInformationReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.orgGuid}/sbSchoolInformation/quickEntry`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];
        const tags: TagType[] = [
          tagFactory.SbEducationRecords.sbEducationRecords(arg.orgGuid),
        ];

        if (arg.personGuid) {
          tags.push(
            tagFactory.CompletePersonProfile.personProfile(arg.personGuid),
          );
        }

        return tags;
      },
    }),

    /**
     * @esbEndpoint POST /advancements/:orgGuid/sbExtendedProfiles
     */
    getSbExtendedProfiles: builder.query<
      PostGetSbExtendedProfilesRes,
      {
        orgGuid: string;
        body: PostGetSbExtendedProfilesReq;
      }
    >({
      query: arg => ({
        url: `/advancements/${arg.orgGuid}/sbExtendedProfiles`,
        method: 'POST',
        body: arg.body,
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];
        const tags = [
          tagFactory.SbExtendedProfiles.SbExtendedProfiles(arg.orgGuid),
        ];

        return tags;
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/:orgGuid/sbExtendedProfile
     */
    postSbExtendedProfile: builder.mutation<
      PostSbExtendedProfileReqRes,
      {
        orgGuid: string;
        personGuid?: string;
        body: PostSbExtendedProfileReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.orgGuid}/sbExtendedProfile`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];
        const tags: TagType[] = [
          tagFactory.SbExtendedProfiles.SbExtendedProfiles(arg.orgGuid),
        ];

        if (arg.personGuid) {
          tags.push(
            tagFactory.CompletePersonProfile.personProfile(arg.personGuid),
          );
        }

        return tags;
      },
    }),

    /**
     * @esbEndpoint PUT /persons/v2/:personGuid/profile
     */
    updateAkelaProfile: builder.mutation<
      void,
      {
        personGuid: string;
        body: PutAkelaProfileRequest;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/profile`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              { type: tagTypes.CompletePersonProfile, id: arg.personGuid },
              {
                type: tagTypes.UnitRoster,
                id: `RosterAdult-${arg.personGuid}`,
              },
            ],
    }),

    /**
     * @esbEndpoint PUT /advancements/users/:userId
     */
    updateSBProfile: builder.mutation<
      void,
      {
        userId: number;
        body: PutSBProfileRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/users/${arg.userId}`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error ? [] : [{ type: tagTypes.PersonProfile, id: arg.userId }],
    }),

    /**
     * @esbEndpoint POST /persons/v2/:personGuid/deleteProfileInfo
     */
    deleteProfileInfo: builder.mutation<
      void,
      {
        personGuid: string;
        body: PostDeleteProfileInfoRequest;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/deleteProfileInfo`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              { type: tagTypes.CompletePersonProfile, id: arg.personGuid },
              {
                type: tagTypes.UnitRoster,
                id: `RosterAdult-${arg.personGuid}`,
              },
            ],
    }),

    /**
     * @esbEndpoint PUT /persons/:personGuid/subscriptions/:subscriptionId
     */
    updateSubscription: builder.mutation<
      void,
      {
        subscriptionId: number;
        personGuid: string;
        body: PUTSubscriptionRequest;
      }
    >({
      query: arg => ({
        url: `/persons/${arg.personGuid}/subscriptions/${arg.subscriptionId}`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [{ type: tagTypes.PersonSubscriptions, id: arg.personGuid }],
    }),

    /**
     * @esbEndpoint POST /persons/:personGuid/subscriptions
     */
    createSubscription: builder.mutation<
      void,
      {
        personGuid: string;
        body: POSTSubscriptionRequest;
      }
    >({
      query: arg => ({
        url: `/persons/${arg.personGuid}/subscriptions`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [{ type: tagTypes.PersonSubscriptions, id: arg.personGuid }],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/youth/awards/:awardId/requirements
     */
    updateYouthAwardRequirements: builder.mutation<
      PostAwardRequirementsResponse,
      {
        awardId: number;
        body: PostAwardRequirementsRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/awards/${arg.awardId}/requirements`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_res, _error, arg) => {
        const tags = [];
        tags.push(
          tagFactory.AwardUserRequirements.orgRequirements(
            arg.body[0].organizationGuid!,
            arg.awardId,
          ),
        );

        return tags;
      },
    }),

    /**
     * This is the V2 for awards used ONLY FOR EAGLE PALMS and NOA
     * @esbEndpoint POST /advancements/v2/users/awards/:awardId
     */
    postUserAward: builder.mutation<
      PostUserAwardResponse,
      {
        awardId: number;
        body: PostUserAwardRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/users/awards/${arg.awardId}`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_res, error, arg) => {
        if (error) return [];

        const awardId = arg.awardId;

        const tags = arg.body.flatMap(item => {
          const userId = item.userId;
          return [
            tagFactory.YouthAward.youthAwardsList(userId),
            tagFactory.YouthAwardRequirements.youthRequirements(
              userId,
              awardId,
            ),
          ];
        });
        return tags;
      },
    }),

    /**
     * This is the V2 for awards used ONLY FOR EAGLE PALMS and NOA
     * @esbEndpoint POST /advancements/v2/users/awards/:awardId/requirements
     */
    postUserAwardRequirements: builder.mutation<
      PostUserAwardRequirementResponse,
      { body: PostUserAwardRequirementRequest; awardId: number }
    >({
      query: arg => ({
        url: `/advancements/v2/users/awards/${arg.awardId}/requirements`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_res, _error, arg) => {
        const tags = [];
        tags.push(
          tagFactory.AwardUserRequirements.orgRequirements(
            arg.body[0].organizationGuid,
            arg.awardId,
          ),
        );
        tags.push(
          ...arg.body.map(item =>
            tagFactory.YouthAward.youthAwardsList(item.userId),
          ),
          ...arg.body.map(
            item =>
              tagFactory.YouthAwardRequirements.youthRequirements(
                item.userId,
                arg.awardId,
              ),
            tagFactory.AwardUserRequirements.orgRequirements(
              arg.body[0].organizationGuid,
              arg.awardId,
            ),
          ),
        );
        return tags;
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/awards/:awardId/requirements
     */
    getYouthAwardRequirements: builder.query<
      // TODO: type this
      void,
      {
        userId: number;
        awardId: number;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/awards/${arg.awardId}/requirements`,
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];
        const tags = [
          tagFactory.YouthAwardRequirements.youthRequirements(
            arg.userId,
            arg.awardId,
          ),
        ];

        return tags;
      },
    }),

    /**
     * @deprecated use V2 instead, see postUserAward
     *
     * @esbEndpoint POST /advancements/youth/awards
     */
    createYouthAward: builder.mutation<
      PostAwardResponse,
      {
        body: PostAwardRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/youth/awards`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_res, error, arg) => {
        if (error) return [];
        const tags = arg.body.flatMap(item => {
          const userId = item.userId;
          const items = item.awards.flatMap(award => {
            const awardId = award.id;
            return [
              tagFactory.YouthAward.youthAwardsList(userId),
              tagFactory.YouthAwardRequirements.youthRequirements(
                userId,
                awardId,
              ),
            ];
          });
          return items;
        });
        return tags;
      },
    }),

    /**
     * @esbEndpoint PUT /advancements/youth/:userId/awards/:awardId/:userAwardId
     */
    updateYouthAward: builder.mutation<
      void,
      {
        userId: number;
        awardId: number;
        userAwardId: number;
        body: PutUpdateAwardsRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/youth/${arg.userId}/awards/${arg.awardId}/${arg.userAwardId}`,
        method: 'PUT',
        body: arg.body,
      }),
    }),

    /**
     * @esbEndpoint PUT /advancements/v2/youth/:userId:/awards/:awardId
     */
    updateYouthAwardV2: builder.mutation<
      void,
      {
        userId: number;
        awardId: number;
        body: PutUpdateYouthAwardRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/awards/${arg.awardId}`,
        method: 'PUT',
        body: arg.body,
      }),
    }),

    /**
     * Advancement-summary save (mark completed/approved/awarded, set completion
     * date) for ranks, adventures and merit badges. Awards keep their dedicated
     * mutations; unapproving routes through `unapproveYouthAward`.
     * @esbEndpoint PUT /advancements/youth/:userId/:advancementType/:advancementId
     */
    updateYouthAdvancement: builder.mutation<
      PutUpdateYouthAdvancementResponse,
      {
        userId: number | string;
        advancementType: AdvancementTypesType;
        advancementId: number | string;
        organizationGuid?: string;
        body: PutUpdateYouthAdvancementRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/youth/${arg.userId}/${arg.advancementType}/${arg.advancementId}`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const listTagByType = {
          [advancementTypes.RANKS]: tagFactory.YouthRanks.youthRanksList,
          [advancementTypes.MERIT_BADGES]:
            tagFactory.YouthMeritBadge.youthMBList,
          [advancementTypes.AWARDS]: tagFactory.YouthAward.youthAwardsList,
          [advancementTypes.ADVENTURES]:
            tagFactory.YouthAdventure.youthAdventuresList,
        } as const;
        const listTag = listTagByType[
          arg.advancementType as keyof typeof listTagByType
        ]?.(arg.userId);

        return [
          ...(listTag ? [listTag] : []),
          ...(arg.organizationGuid
            ? [
                tagFactory.AdvancementHistory.unitHistory(arg.organizationGuid),
                tagFactory.OrganizationAdvDashboard.unitDashboard(
                  arg.organizationGuid,
                ),
              ]
            : []),
        ];
      },
    }),

    /**
     * @esbEndpoint PUT /advancements/unapprove
     */
    unapproveYouthAward: builder.mutation<
      void,
      {
        organizationGuid?: string;
        advancementType?: string;
        body: PostUnapproveAdvancementRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/unapprove`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        // Refresh the youth's per-type advancement list so a stepped-down item
        // shows its new status (the roster-lite details page reads it from there)
        const listTagByType = {
          [advancementTypes.RANKS]: tagFactory.YouthRanks.youthRanksList,
          [advancementTypes.MERIT_BADGES]:
            tagFactory.YouthMeritBadge.youthMBList,
          [advancementTypes.AWARDS]: tagFactory.YouthAward.youthAwardsList,
          [advancementTypes.ADVENTURES]:
            tagFactory.YouthAdventure.youthAdventuresList,
        } as const;
        const youthUserId = arg.body?.[0]?.userId;
        const youthListTag =
          arg.advancementType != null && youthUserId != null
            ? listTagByType[
                arg.advancementType as keyof typeof listTagByType
              ]?.(String(youthUserId))
            : undefined;

        return [
          /** refresh to purchase step 1 list **/
          ...(arg.organizationGuid
            ? [
                tagFactory.AdvancementHistory.unitHistoryWithStatus(
                  arg.organizationGuid,
                  toPurchaseStatusesString,
                ),
              ]
            : []),
          {
            // invalidate the counts to approve tab
            type: tagTypes.OrganizationAdvDashboard,
            id: arg.organizationGuid,
          },
          arg.advancementType
            ? {
                // refresh to approve list
                type: tagTypes.AdvancementHistory,
                id: `Unit-${arg.organizationGuid}-AdvType-${arg.advancementType}`,
              }
            : {
                // refresh to approve list
                type: tagTypes.AdvancementHistory,
                id: `Unit-List-${arg.organizationGuid}`,
              },
          ...(youthListTag ? [youthListTag] : []),
        ];
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/activities
     */
    getActivities: builder.query<
      GetActivitiesResponse,
      {
        body: GetActivitesRequest;
        params: GetActivitesRequestParams;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/activities`,
        method: 'POST',
        params: arg.params,
        body: arg.body,
      }),
      providesTags: (result, error, arg) => {
        if (error) return [];

        const tags = [];
        if (arg.body.hostOrganizationGuid) {
          tags.push(
            tagFactory.Activities.orgActivities(arg.body.hostOrganizationGuid),
          );
        }

        if (arg.body.personGuid) {
          tags.push(tagFactory.Activities.youthActivities(arg.body.personGuid));
        }
        const tagPerActivity =
          result?.activities?.map(activity =>
            tagFactory.Activities.activityItem(activity.id),
          ) || [];
        tags.push(...tagPerActivity);

        return tags;
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/:userId/userActivitySummary
     */
    getUserActivitySummary: builder.query<
      GetUserActivitySummaryResponse,
      {
        userId: number | string;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.userId}/userActivitySummary`,
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];

        return [
          {
            id: `${arg.userId}`,
            type: tagTypes.UserActivitySummary,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint GET /persons/v2/:personGuid/trainings/ypt
     */
    getPersonTrainingsYPT: builder.query<
      GetPersonTrainingYPTResponse,
      {
        personGuid: string;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/trainings/ypt`,
        method: 'GET',
      }),
      keepUnusedDataFor: 900, // 15 min
      providesTags: (_result, error, arg) => {
        if (error) return [];

        return [
          {
            id: arg.personGuid,
            type: tagTypes.PersonTrainingYPT,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/:organizationGuid/denChiefs
     */
    getUnitDenChiefs: builder.query<
      GetUnitDenChiefsRes[],
      {
        orgGuids: string[];
      }
    >({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        const requests = arg.orgGuids.map(orgGuid =>
          baseQuery({
            url: `/organizations/v2/${orgGuid}/denChief`,
            method: 'GET',
          }),
        );

        const responses = await Promise.all(requests);
        const datas = responses
          .filter(item => !!item.data)
          .map(item => item.data) as GetUnitDenChiefsRes[];
        return { data: datas };
      },
      providesTags: (result, error, arg) => {
        if (!result || error) return [];
        const orgs = _.chain(arg.orgGuids).uniq().sort().join(',').value();

        return [
          {
            type: tagTypes.UnitDenchiefs,
            id: `Unit-Denchiefs-${orgs}`,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/:organizationGuid/subUnits
     */
    getSubUnits: builder.query<
      GetOrgSubUnitsResponse,
      {
        orgGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.orgGuid}/subUnits`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => {
        if (!result || error) return [];

        return [tagFactory.SubUnits.subUnits(arg.orgGuid)];
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/:organizationGuid/subUnitAdults
     */
    getSubUnitAdults: builder.query<
      GetSubUnitAdultsResponse,
      {
        orgGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.orgGuid}/subUnitAdults`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => {
        if (!result || error) return [];

        return [
          tagFactory.SubUnits.adults(arg.orgGuid),
          tagFactory.SubUnits.forOrg(arg.orgGuid),
        ];
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/:organizationGuid/subUnitYouths
     */
    getSubUnitYouths: builder.query<
      GetSubUnitYouthsResponse,
      {
        orgGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.orgGuid}/subUnitYouths`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => {
        if (!result || error) return [];

        return [
          tagFactory.SubUnits.youths(arg.orgGuid),
          tagFactory.SubUnits.forOrg(arg.orgGuid),
        ];
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/:organizationGuid/subUnits
     * For multiple units at once
     */
    getMultipleSubUnits: builder.query<
      GetOrgSubUnitsResponse[],
      {
        orgGuids: string[];
      }
    >({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        const requests = arg.orgGuids.map(orgGuid =>
          baseQuery({
            url: `/organizations/v2/units/${orgGuid}/subUnits`,
            method: 'GET',
          }),
        );

        const responses = await Promise.all(requests);
        const datas = responses
          .filter(item => !!item.data)
          .map(item => item.data) as GetOrgSubUnitsResponse[];
        return { data: datas };
      },
      providesTags: (result, error, arg) => {
        if (!result || error) return [];
        const orgs = _.chain(arg.orgGuids).uniq().sort().join(',').value();

        return [
          {
            type: tagTypes.SubUnits,
            id: `SubUnits-${orgs}`,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint POST /organizations/v2/units/:organizationGuid/:subUnitType/add
     */
    createSubUnit: builder.mutation<
      PostAddSubUnitResponse,
      {
        orgGuid: string;
        subUnitType: string;
        body: PostAddSubUnitRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.orgGuid}/${arg.subUnitType}/add`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_res, error, arg) =>
        error
          ? []
          : [
              tagFactory.SubUnits.subUnits(arg.orgGuid),
              tagFactory.SubUnits.forOrg(arg.orgGuid),
              tagFactory.UnitRoster.organizationRosters(arg.orgGuid),
            ],
    }),

    /**
     * @esbEndpoint PUT /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId
     */
    UpdateSubUnit: builder.mutation<
      PutSubUnitResponse,
      {
        orgGuid: string;
        subUnitType: string;
        subUnitId: number;
        body: PutSubUnitRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.orgGuid}/${arg.subUnitType}/${arg.subUnitId}`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_res, error, arg) =>
        error
          ? []
          : [
              tagFactory.SubUnits.subUnits(arg.orgGuid),
              tagFactory.SubUnits.forOrg(arg.orgGuid),
              tagFactory.UnitRoster.organizationRosters(arg.orgGuid),
            ],
    }),

    /**
     * @esbEndpoint POST /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId/youths
     */
    addYouthsToSubUnit: builder.mutation<
      PostAddYouthsToSubUnitResponse,
      {
        orgGuid: string;
        subUnitType: string;
        subUnitId: number;
        body: PostAddYouthsToSubUnitRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.orgGuid}/${arg.subUnitType}/${arg.subUnitId}/youths`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_res, error, arg) =>
        error
          ? []
          : [
              tagFactory.SubUnits.forOrg(arg.orgGuid),
              tagFactory.UnitRoster.organizationRosters(arg.orgGuid),
            ],
    }),

    /**
     * @esbEndpoint POST /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId/adults
     */
    addAdultsToSubUnit: builder.mutation<
      PostAddAdultsToSubUnitResponse,
      {
        orgGuid: string;
        subUnitType: string;
        subUnitId: number;
        body: PostAddAdultsToSubUnitRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.orgGuid}/${arg.subUnitType}/${arg.subUnitId}/adults`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_res, error, arg) =>
        error
          ? []
          : [
              tagFactory.SubUnits.forOrg(arg.orgGuid),
              tagFactory.UnitRoster.organizationRosters(arg.orgGuid),
            ],
    }),

    /**
     * @esbEndpoint DELETE /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId
     */
    deleteSubUnit: builder.mutation<
      DeleteSubUnitResponse,
      {
        orgGuid: string;
        subUnitType: string;
        subUnitId: number;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.orgGuid}/${arg.subUnitType}/${arg.subUnitId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, error, arg) =>
        error
          ? []
          : [
              tagFactory.SubUnits.subUnits(arg.orgGuid),
              tagFactory.SubUnits.forOrg(arg.orgGuid),
              tagFactory.UnitRoster.organizationRosters(arg.orgGuid),
            ],
    }),

    /**
     * @esbEndpoint DELETE /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId/users
     */
    deleteUserFromSubUnit: builder.mutation<
      DeleteUserFromSubUnitResponse,
      {
        orgGuid: string;
        subUnitType: string;
        subUnitId: number;
        usersToDelete: number[];
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.orgGuid}/${arg.subUnitType}/${
          arg.subUnitId
        }/users?userId=${arg.usersToDelete.toString()}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, error, arg) =>
        error
          ? []
          : [
              tagFactory.SubUnits.forOrg(arg.orgGuid),
              tagFactory.UnitRoster.organizationRosters(arg.orgGuid),
            ],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/:organizationGuid/purchaseOrders
     */
    getUnitPurchaseOrders: builder.query<
      GetUnitPurchaseOrdersResponse,
      {
        organizationGuid: string;
        params: GetUnitPurchaseOrdersRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.organizationGuid}/purchaseOrders`,
        method: 'GET',
        params: arg.params,
      }),
      transformResponse: (
        response: GetUnitPurchaseOrdersResponse,
      ): GetUnitPurchaseOrdersResponse =>
        response.map(po => ({
          ...po,
          status: po.status.toLowerCase(),
        })),
      providesTags: (result, error, arg) => {
        if (error || !result) return [];

        return [
          tagFactory.PurchaseOrders.purchaseOrderList(arg.organizationGuid),
        ];
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/:organizationGuid/purchaseOrders/:purchaseOrderId
     */
    getPurchaseOrderDetails: builder.query<
      GetPurchaseOrderDetailsResponse,
      {
        organizationGuid: string;
        purchaseOrderId: number;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.organizationGuid}/purchaseOrders/${arg.purchaseOrderId}`,
        method: 'GET',
      }),
      transformResponse: (
        res: GetPurchaseOrderDetailsResponse,
      ): GetPurchaseOrderDetailsResponse => ({
        ...res,
        status: res.status.toLowerCase(),
      }),
      providesTags: (result, error, arg) => {
        if (error || !result) return [];

        return [
          tagFactory.PurchaseOrders.purchaseOrderDetails(arg.purchaseOrderId),
        ];
      },
    }),

    /**
     * @esbEndpoint PUT /advancements/v2/:organizationGuid/purchaseOrders/:id
     */
    updatePurchaseOrder: builder.mutation<
      void,
      {
        organizationGuid: string;
        purchaseOrderId: number;
        body: UpdatePurchaseOrderDetailsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.organizationGuid}/purchaseOrders/${arg.purchaseOrderId}`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              tagFactory.PurchaseOrders.purchaseOrderList(arg.organizationGuid),
              tagFactory.PurchaseOrders.purchaseOrderDetails(
                arg.purchaseOrderId,
              ),
            ],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/:organizationGuid/purchaseOrders/:purchaseOrderId/items
     */
    updateItemPurchaseOrder: builder.mutation<
      void,
      {
        organizationGuid: string;
        purchaseOrderId: number;
        body: AddItemPurchaseOrderReq[];
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.organizationGuid}/purchaseOrders/${arg.purchaseOrderId}/items`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];
        return [
          /** refresh to purchase step 1 list **/
          tagFactory.AdvancementHistory.unitHistoryWithStatus(
            arg.organizationGuid,
            toPurchaseStatusesString,
          ),
          tagFactory.PurchaseOrders.purchaseOrderDetails(arg.purchaseOrderId),
          tagFactory.PurchaseOrders.purchaseOrderList(arg.organizationGuid),
        ];
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/:organizationGuid/purchaseOrders
     */
    createPurchaseOrder: builder.mutation<
      {
        message: string;
        id: number;
      },
      {
        organizationGuid: string;
        body: {
          userId: number;
        };
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.organizationGuid}/purchaseOrders`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [tagFactory.PurchaseOrders.purchaseOrderList(arg.organizationGuid)],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/:organizationGuid/purchaseOrders/:poid/items/delete
     */
    postDeleteItemPurchaseOrder: builder.mutation<
      {
        userId: number;
        advancementType: string;
        advancementId?: number;
        userAdvancementId?: number;
        status?: string;
        message?: string;
      },
      {
        organizationGuid: string;
        purchaseOrderId: number;
        body: AddItemPurchaseOrderReq[];
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.organizationGuid}/purchaseOrders/${arg.purchaseOrderId}/items/delete`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_results, error, arg) => {
        if (error) return [];
        return [
          tagFactory.PurchaseOrders.purchaseOrderDetails(arg.purchaseOrderId),
          /** refresh to purchase step 1 list **/
          tagFactory.AdvancementHistory.unitHistoryWithStatus(
            arg.organizationGuid,
            toPurchaseStatusesString,
          ),
        ];
      },
    }),

    /**
     * @esbEndpoint POST /organizations/:organizationGuid/advancementsReadyToBeAwarded
     */
    getAdvancementsReadyAward: builder.query<
      AdvancementReportData,
      {
        organizationGuid: string;
        body: GetOrgAdvancementsReadyToAwardRequest;
        params: GetOrgAdvancementsReadyToAwardParams;
      }
    >({
      query: arg => ({
        url: `/organizations/${arg.organizationGuid}/advancementsReadyToBeAwarded`,
        method: 'POST',
        body: arg.body,
        params: arg.params,
      }),
      providesTags: (_reslt, error, arg) => {
        if (error) return [];
        return [
          {
            type: tagTypes.OrgAdvancementsReadyToAward,
            id: arg.organizationGuid,
          },
        ];
      },
      transformResponse: (response: GetOrgAdvancementsReadyToAwardResponse) =>
        mapAdvancementReportDtoToModel(response || []),
    }),

    /*
     * @esbEndpoint GET /organizations/positions/:organizationGuid
     */
    getOrgPositions: builder.query<
      GetOrgPositionsResponse,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/positions/${arg.organizationGuid}`,
        method: 'GET',
      }),
      transformResponse: (
        res: GetOrgPositionsResponse,
      ): GetOrgPositionsResponse => ({
        ...res,
        Positions: res.Positions.map(pos => {
          // Executive Officer should have max 1 but data team can not change it for some reason see SBL-5567
          if (pos.positionLong === Positions.EO) {
            return { ...pos, max: 1 };
          }

          return pos;
        }),
      }),
      providesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.OrganizationPositions,
                id: arg.organizationGuid,
              },
            ],
    }),

    /**
     * @esbEndpoint POST /organizations/positions/:orgGuid/reassignPositions
     */
    postReassignPositions: builder.mutation<
      PostReassignPositionsResponse,
      {
        orgGuid: string;
        body: PostReassignPositionsRequest;
        shouldInvalidateTags?: boolean;
      }
    >({
      query: arg => ({
        url: `/organizations/positions/${arg.orgGuid}/reassignPositions`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        if (arg.shouldInvalidateTags) {
          return [
            tagFactory.OrganizationPositions.organizationPositions(arg.orgGuid),
            tagFactory.OrganizationRoster.adultRoster(arg.orgGuid),
          ];
        }

        return [];
      },
    }),

    /**
     * @esbEndpoint POST /persons/positions/:personGuid/assignFunctional
     */
    postAssignFunctionalPositions: builder.mutation<
      PostAssignFunctionalResponse,
      {
        personGuid: string;
        body: PostAssignFunctionalRequest;
        shouldInvalidateTags?: boolean;
        isYouth?: boolean;
      }
    >({
      query: arg => ({
        url: `/persons/positions/${arg.personGuid}/assignFunctional`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        if (arg.isYouth && arg.shouldInvalidateTags) {
          return [
            tagFactory.OrganizationRoster.youthRoster(
              arg.body.OrganizationGuid,
            ),
          ];
        }

        if (arg.shouldInvalidateTags) {
          return [
            tagFactory.OrganizationPositions.organizationPositions(
              arg.body.OrganizationGuid,
            ),
            tagFactory.OrganizationRoster.adultRoster(
              arg.body.OrganizationGuid,
            ),
          ];
        }
        return [];
      },
    }),

    /**
     * @esbEndpoint POST /persons/positions/:personGuid/expireFunctional
     */
    postExpireFunctionalPositions: builder.mutation<
      PostExpireFunctionalResponse,
      {
        personGuid: string;
        body: PostExpireFunctionalRequest;
        shouldInvalidateTags?: boolean;
        isYouth?: boolean;
      }
    >({
      query: arg => ({
        url: `/persons/positions/${arg.personGuid}/expireFunctional`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        if (arg.isYouth && arg.shouldInvalidateTags) {
          return [
            tagFactory.OrganizationRoster.youthRoster(
              arg.body.OrganizationGuid,
            ),
          ];
        }

        if (arg.shouldInvalidateTags) {
          return [
            tagFactory.OrganizationPositions.organizationPositions(
              arg.body.OrganizationGuid,
            ),
            tagFactory.OrganizationRoster.adultRoster(
              arg.body.OrganizationGuid,
            ),
          ];
        }
        return [];
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/events/:eventId/permissionSlipProfiles
     */
    postPermissionSlipProfiles: builder.mutation<
      PostPermissionSlipProfilesResponse,
      {
        eventId: number;
        body: PostPermissionSlipProfilesRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/events/${arg.eventId}/permissionSlipProfiles`,
        method: 'POST',
        body: arg.body,
        responseHandler: response => response.blob(),
      }),
    }),

    /**
     * @esbEndpoint POST /persons/:personGuid/membershipRegistrations
     */
    getMembershipRegistrations: builder.query<
      PostMembershipRegistrationsResponse,
      {
        personGuid: string;
        body: PostMembershipRegistrationsRequest;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/membershipRegistrations`,
        method: 'POST',
        body: arg.body,
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];

        return [
          {
            id: arg.personGuid,
            type: tagTypes.MembershipRegistration,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint PUT /persons/:personGuid/membershipRegistrations/:registrationId
     */
    updateMembershipRegistration: builder.mutation<
      UpdateMembershipRegistrationResponse,
      {
        personGuid: string;
        registrationId: number;
        body: UpdateMembershipRegistrationRequest;
      }
    >({
      query: arg => ({
        url: `/persons/${arg.personGuid}/membershipRegistrations/${arg.registrationId}`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        return [
          { id: arg.personGuid, type: tagTypes.MembershipRegistration },
          tagFactory.OrganizationRoster.youthInRoster(arg.personGuid),
          tagFactory.OrganizationRoster.personInRoster(arg.personGuid),
          tagFactory.CompletePersonProfile.personProfile(arg.personGuid),
        ];
      },
    }),

    /**
     * @esbEndpoint GET /advancements/awards/:awardId/requirements
     */
    getAwardRequirements: builder.query<
      GetAwardRequirementsResponse,
      {
        awardId: number;
        params?: {
          versionId: number;
        };
      }
    >({
      query: arg => ({
        url: `/advancements/awards/${arg.awardId}/requirements`,
        params: arg.params,
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];

        return [
          {
            id: arg.awardId,
            type: tagTypes.AwardRequirements,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint GET /events/communications/organizations
     */
    getEventsCommunicationsOrganizations: builder.query<
      GetEventsCommunicationsOrganizationsResponse,
      GetEventsCommunicationsOrganizationsRequestParams
    >({
      query: arg => ({
        url: `/events/communications/organizations`,
        params: arg,
      }),
      providesTags: (result, error, arg) => {
        if (error) return [];

        return [
          tagFactory.EventsCommunications.organizationGuid(
            arg.organizationGuid,
          ),
          ...(result?.events || []).map(({ eventGuid }) =>
            tagFactory.EventsCommunications.event(eventGuid),
          ),
        ];
      },
    }),

    /**
     * @esbEndpoint POST /events/communications
     */
    postEventsCommunications: builder.mutation<
      PostEventsCommunicationsResponse,
      PostEventsCommunicationsRequestBody
    >({
      query: arg => ({
        url: `/events/communications`,
        method: 'POST',
        body: arg,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        return arg.targetOrganizations.map(({ organizationGuid }) =>
          tagFactory.EventsCommunications.organizationGuid(organizationGuid),
        );
      },
    }),

    /**
     * @esbEndpoint DELETE /events/communications
     */
    deleteEventsCommunications: builder.mutation<
      DeleteEventsCommunicationsResponse,
      DeleteEventsCommunicationsRequestParams
    >({
      query: arg => ({
        url: `/events/communications/${arg.eventGuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        return [tagFactory.EventsCommunications.event(arg.eventGuid)];
      },
    }),

    /**
     * @esbEndpoint GET /registrations/v2/precisely/token
     */
    authenticatePrecisely: builder.query<
      PreciselyAuthTokenResponse,
      {
        params: PreciselyAuthTokenQueryParams;
      }
    >({
      query: arg => ({
        url: '/registrations/v2/precisely/token',
        method: 'GET',
        params: arg.params,
      }),
      keepUnusedDataFor: 60 * 20, // 20 minutes
      providesTags: (result, error) =>
        result && !error
          ? [
              {
                type: tagTypes.PreciselyToken,
              },
            ]
          : [],
    }),
  }),
});

export const endpoints = esbApi.endpoints;

export const {
  // Persons
  useGetPersonTrainingsYPTQuery,
  useLazyGetPersonTrainingsYPTQuery,
  useGetUserActivitySummaryQuery,
  useLazyGetUserActivitySummaryQuery,
  useGetMembershipRegistrationsQuery,
  useLazyGetMembershipRegistrationsQuery,
  useUpdateMembershipRegistrationMutation,
  // Org Dashboard
  useGetOrgAdvancementsDashboardQuery,
  useLazyGetOrgAdvancementsDashboardQuery,
  // Act Dashboard
  useGetOrgActivityDashboardQuery,
  useLazyGetOrgActivityDashboardQuery,
  // Advancements
  useGetRankRequirementsQuery,
  useGetRankDetailsQuery,
  useGetGeneralAdvancementDetailsQuery,
  useGetSSElectiveDetailsQuery,
  useGetYouthRankDetailsQuery,
  useGetYouthRankRequirementsQuery,
  useGetOrganizationProfileQuery,
  useGetBulkUserRankRequirementsQuery,
  // Merit Badges
  useGetMeritBadgeRequirementsQuery,
  useLazyGetMeritBadgeRequirementsQuery,
  useGetBulkUserMeritBadgeRequirementsQuery,
  useLazyGetBulkUserMeritBadgeRequirementsQuery,
  useGetYouthMeritBadgeDetailsQuery,
  useUpdateYouthMeritBadgeVersionMutation,
  useUpdateYouthRankVersionMutation,
  usePostMBCounselorsSearchQuery,
  useLazyPostMBCounselorsSearchQuery,
  // Collections
  useGetRanksQuery,
  useLazyGetRanksQuery,
  useGetAdventuresQuery,
  useLazyGetAdventuresQuery,
  // AdvancementHistory
  useGetAdvancementHistoryQuery,
  useLazyGetAdvancementHistoryQuery,
  // Adventures
  useGetAdventureRequirementsQuery,
  useLazyGetAdventureRequirementsQuery,
  useGetYouthAdventureRequirementsQuery,
  useGetBulkUserAdventureRequirementsQuery,
  useLazyGetBulkUserAdventureRequirementsQuery,
  usePostYouthAdventuresRequirementsMutation,
  // Persons
  useGetPersonRenewalRelationshipsQuery,
  useLazyGetPersonRenewalRelationshipsQuery,
  useLazyGetOrganizationProfileQuery,
  useLazyGetPersonProfileQuery,
  useGetPersonProfileQuery,
  useGetCompletePersonProfileQuery,
  useLazyGetCompletePersonProfileQuery,
  useGetUserSubscriptionsQuery,
  useGetUserMystProfileQuery,
  // Mutations
  usePostBulkYouthRankRequirementsMutation,
  usePostYouthMeritBadgesMutation,
  usePostBulkYouthMeritBadgeRequirementsMutation,
  usePostYouthRankRequirementsMutation,
  // Awards
  useCreateYouthAwardMutation,
  useUpdateYouthAwardRequirementsMutation,
  useUpdateYouthAwardMutation,
  useUpdateYouthAwardV2Mutation,
  useUpdateYouthAdvancementMutation,
  useUnapproveYouthAwardMutation,
  // Activities
  useGetActivitiesQuery,
  useLazyGetActivitiesQuery,
  //Unit Den Chiefs
  useGetUnitDenChiefsQuery,
  //Sub units
  useGetSubUnitsQuery,
  useGetSubUnitAdultsQuery,
  useLazyGetSubUnitAdultsQuery,
  useGetSubUnitYouthsQuery,
  useLazyGetSubUnitYouthsQuery,
  useGetMultipleSubUnitsQuery,
  useCreateSubUnitMutation,
  useAddAdultsToSubUnitMutation,
  useAddYouthsToSubUnitMutation,
  useDeleteUserFromSubUnitMutation,
  useDeleteSubUnitMutation,
  // PurchaseOrders
  useGetUnitPurchaseOrdersQuery,
  useLazyGetUnitPurchaseOrdersQuery,
  useGetPurchaseOrderDetailsQuery,
  useLazyGetPurchaseOrderDetailsQuery,
  useUpdatePurchaseOrderMutation,
  useUpdateItemPurchaseOrderMutation,
  useCreatePurchaseOrderMutation,
  usePostDeleteItemPurchaseOrderMutation,
  // Ready to award
  useGetAdvancementsReadyAwardQuery,
  useLazyGetAdvancementsReadyAwardQuery,
  //  OrgPositions
  useGetOrgPositionsQuery,
  useLazyGetOrgPositionsQuery,
  usePostReassignPositionsMutation,
  usePostAssignFunctionalPositionsMutation,
  usePostExpireFunctionalPositionsMutation,
  // Permission Slip
  usePostPermissionSlipProfilesMutation,
  // Profile
  useCreateProfilePictureMutation,
  useUpdateProfilePictureMutation,
  useCreatePictureUploadMutation,
  useUpdateAkelaProfileMutation,
  useUpdateSBProfileMutation,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useDeleteProfileInfoMutation,
  useGetEducationRecordsQuery,
  usePostSbSchoolInformationMutation,
  useGetSbExtendedProfilesQuery,
  usePostSbExtendedProfileMutation,
  // Payments
  useLazyGetOrganizationOnboardUrlQuery,
  useGetOrganizationPaymentProfileQuery,
  usePostOrganizationPaymentProfileMutation,
  usePutOrganizationPaymentProfileMutation,
  // Awards
  useGetAwardDetailsQuery,
  useLazyGetAwardDetailsQuery,
  useGetAwardRequirementsQuery,
  useLazyGetAwardRequirementsQuery,
  useGetBulkUserAwardRequirementsQuery,
  useLazyGetBulkUserAwardRequirementsQuery,
  usePostUserAwardMutation,
  useGetYouthAwardRequirementsQuery,
  useLazyGetYouthAwardRequirementsQuery,
  usePostUserAwardRequirementsMutation,
  // Events
  useGetEventsCommunicationsOrganizationsQuery,
  useLazyGetEventsCommunicationsOrganizationsQuery,
  useDeleteEventsCommunicationsMutation,
  usePostEventsCommunicationsMutation,
} = esbApi;
